<template>
  <section class="questions" v-data-test-id="'questions'">
    <div class="questions__inner">
      <LoadingIndicator v-if="isLoadingFirstQuestions" class="questions__loader" />
      <div v-else-if="questionTotal > 0" class="questions__wrapper">
        <transition-group name="slide-and-appear" tag="div">
          <div
            v-for="question in questions"
            :key="question.id"
            ref="questionBlock"
            class="questions__question-block"
            v-data-test-id="'question-block'"
          >
            <p class="questions__question">{{ $t('questions_initial') }} {{ question.content }}</p>
            <p class="questions__question-author">{{ question.author }}</p>
            <div v-if="question.answers.length > 0" class="questions__answers-wrapper">
              <div
                v-for="answer in question.answers"
                :key="answer.id"
                class="questions__answer-block"
                v-data-test-id="'answer-block'"
              >
                <p class="questions__answer">{{ $t('answers_initial') }} {{ answer.content }}</p>
                <p v-if="answer.storeOwnerComment" class="questions__answer-author">
                  {{ $t('on_customer_service') }}
                </p>
                <p class="questions__answer-date">{{ formatISODate(answer.createdAt) }}</p>
              </div>
            </div>
          </div>
        </transition-group>
        <Button
          v-if="hasMoreQuestionsToLoad"
          :color="BUTTON_STYLES.color.white"
          :isSubmitting="isLoadingMoreQuestions"
          type="button"
          class="questions__load-cta"
          v-data-test-id="'question-load-more-cta'"
          @click="onLoadMoreQuestionsClick"
          >{{ $t('load_more') }}</Button
        >
      </div>
      <div v-else class="questions__wrapper--no-questions">
        <h4 class="questions__subheadline">{{ $t('no_questions_available') }}</h4>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import { observeIntersection } from '@/helpers/intersection-observer'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import { scrollSmoothlyToElement } from '@/helpers/scroll'

const SLIDE_AND_APPEAR_TRANSLATION_PX = 90

export default {
  name: 'Questions',

  components: {
    Button,
    LoadingIndicator,
  },

  data() {
    return {
      BUTTON_STYLES,
    }
  },

  computed: {
    ...mapState('product', ['questions', 'questionTotal', 'questionPageIndex']),
    ...mapState('page', ['locale']),

    ...mapGetters({
      isLoadingFirstQuestions: 'product/isLoadingFirstQuestions',
      isLoadingMoreQuestions: 'product/isLoadingMoreQuestions',
      hasMoreQuestionsToLoad: 'product/hasMoreQuestionsToLoad',
      firstQuestionIndexOfPageLastAdded: 'product/firstQuestionIndexOfPageLastAdded',
    }),
  },

  mounted() {
    observeIntersection(this.$el, () => {
      this.loadQuestions()
      this.preloadQuestionOverlay()
    })
  },

  methods: {
    ...mapActions({
      preloadQuestionOverlay: 'overlay/preloadQuestionOverlay',
      loadQuestions: 'product/loadQuestions',
      loadMoreQuestions: 'product/loadMoreQuestions',
    }),

    async onLoadMoreQuestionsClick(event) {
      event.target.blur()
      await this.loadMoreQuestions()
      await this.$nextTick()
      scrollSmoothlyToElement(this.$refs.questionBlock[this.firstQuestionIndexOfPageLastAdded], {
        duration: On.TransitionTimes.slower,
        offset: -SLIDE_AND_APPEAR_TRANSLATION_PX,
      })
    },

    formatISODate(isoDate) {
      const date = new Date(isoDate)
      return new Intl.DateTimeFormat(this.locale).format(date)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Questions';
</style>

<i18n src="@/locales/questions.json">
</i18n>
